// ORCA360 - Audio Concatenation Utility
// Concatenates multiple audio files in the correct order before transcription
// Order: Briefing → Session/Simulator → Debriefing
// Uses server-side concatenation for performance (same datacenter as Supabase)

import Crunker from 'crunker';
import { Mp3Encoder } from '@breezystack/lamejs';
import { supabase } from './supabase';
import { uploadWithTus, shouldUseTus } from './tusUpload';

// API base URL - use relative path in production
const API_BASE_URL = import.meta.env.DEV ? '' : '';

/**
 * Supported audio file extensions
 */
const AUDIO_EXTENSIONS = ['mp3', 'wav', 'm4a', 'ogg', 'webm', 'flac'];

/**
 * Check if a file is an audio file based on its extension
 */
export function isAudioFile(fileName: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  return AUDIO_EXTENSIONS.includes(ext);
}

/**
 * Get the priority order for an audio file based on its name
 * Returns the priority (1=briefing, 2=session, 3=debrief) or 2 (middle) as default
 *
 * IMPORTANT: Check longer/more specific keywords first to avoid substring issues
 * e.g., "debriefing" must be checked before "briefing"
 */
function getAudioPriority(fileName: string): number {
  const lowerName = fileName.toLowerCase();

  // Check keywords in order of specificity (longer keywords first)
  // This prevents "debriefing" from matching "briefing"
  const keywordsInOrder: [string, number][] = [
    // Debriefing variants (check FIRST - priority 3)
    ['debriefing', 3],
    ['debrief', 3],
    ['post-flight', 3],
    ['postflight', 3],
    ['post_flight', 3],
    ['post-sim', 3],
    ['postsim', 3],
    ['post_sim', 3],

    // Briefing variants (check AFTER debrief - priority 1)
    ['briefing', 1],
    ['brief', 1],
    ['pre-flight', 1],
    ['preflight', 1],
    ['pre_flight', 1],
    ['pre-sim', 1],
    ['presim', 1],
    ['pre_sim', 1],

    // Session/Simulator variants (priority 2)
    ['simulator', 2],
    ['session', 2],
    ['training', 2],
    ['recording', 2],
    ['flight', 2],
    ['main', 2],
    ['sim', 2],

    // Review is ambiguous but usually post-session
    ['review', 3],
  ];

  for (const [keyword, priority] of keywordsInOrder) {
    if (lowerName.includes(keyword)) {
      return priority;
    }
  }

  // Default to middle priority (session) if no keyword matches
  return 2;
}

/**
 * Sort audio files in the correct order for concatenation
 * Order: Briefing (1) → Session (2) → Debriefing (3)
 * Files with same priority are sorted alphabetically
 */
export function sortAudioFiles<T extends { file_name: string }>(files: T[]): T[] {
  return [...files].sort((a, b) => {
    const priorityA = getAudioPriority(a.file_name);
    const priorityB = getAudioPriority(b.file_name);

    // Sort by priority first
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    // Same priority: sort alphabetically
    return a.file_name.localeCompare(b.file_name);
  });
}

/**
 * Get the audio type label for display
 */
export function getAudioTypeLabel(fileName: string): string {
  const priority = getAudioPriority(fileName);
  switch (priority) {
    case 1:
      return 'Briefing';
    case 3:
      return 'Debriefing';
    default:
      return 'Session';
  }
}

/**
 * Download an audio file from Supabase storage
 */
async function downloadAudioFile(storagePath: string): Promise<ArrayBuffer> {
  console.log(`[AudioConcat] Downloading: ${storagePath}`);

  const { data, error } = await supabase.storage
    .from('session-files')
    .download(storagePath);

  if (error) {
    throw new Error(`Failed to download ${storagePath}: ${error.message}`);
  }

  if (!data) {
    throw new Error(`No data received for ${storagePath}`);
  }

  return await data.arrayBuffer();
}

export interface AudioFile {
  id: string;
  file_name: string;
  storage_path: string;
  file_size: number;
}

export interface ConcatenationProgress {
  stage: 'downloading' | 'decoding' | 'concatenating' | 'encoding' | 'uploading' | 'complete';
  currentFile?: string;
  fileIndex?: number;
  totalFiles?: number;
  progress: number; // 0-100
}

export interface ConcatenationResult {
  blob?: Blob; // Optional: only set for client-side concatenation
  storagePath: string;
  fileId: string;
  duration: number; // in seconds
  sourceFiles: string[];
  sourceFileIds: string[]; // IDs for deletion
  sourceStoragePaths: string[]; // Storage paths for deletion
}

/**
 * Encode an AudioBuffer to MP3 using lamejs
 * This produces much smaller files than WAV (typically 10-15x smaller)
 *
 * @param audioBuffer - The AudioBuffer to encode
 * @param bitrate - MP3 bitrate in kbps (default: 128)
 * @returns MP3 blob
 */
function encodeAudioBufferToMp3(audioBuffer: AudioBuffer, bitrate: number = 128): Blob {
  const numberOfChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const samples = audioBuffer.length;

  console.log(`[AudioConcat] Encoding to MP3: ${numberOfChannels} channels, ${sampleRate}Hz, ${samples} samples, ${bitrate}kbps`);

  // Create MP3 encoder (stereo or mono)
  const channels = Math.min(numberOfChannels, 2); // lamejs supports max 2 channels
  const mp3encoder = new Mp3Encoder(channels, sampleRate, bitrate);

  // Get audio data from channels
  const leftChannel = audioBuffer.getChannelData(0);
  const rightChannel = numberOfChannels > 1 ? audioBuffer.getChannelData(1) : leftChannel;

  // Convert Float32Array (-1 to 1) to Int16Array (-32768 to 32767)
  const leftSamples = new Int16Array(samples);
  const rightSamples = new Int16Array(samples);

  for (let i = 0; i < samples; i++) {
    // Clamp values to prevent overflow
    const leftSample = Math.max(-1, Math.min(1, leftChannel[i] ?? 0));
    const rightSample = Math.max(-1, Math.min(1, rightChannel[i] ?? 0));

    leftSamples[i] = leftSample < 0 ? leftSample * 0x8000 : leftSample * 0x7FFF;
    rightSamples[i] = rightSample < 0 ? rightSample * 0x8000 : rightSample * 0x7FFF;
  }

  // Encode in chunks (1152 samples per frame for MP3)
  const mp3Data: Uint8Array[] = [];
  const sampleBlockSize = 1152;

  for (let i = 0; i < samples; i += sampleBlockSize) {
    const leftChunk = leftSamples.subarray(i, Math.min(i + sampleBlockSize, samples));
    const rightChunk = rightSamples.subarray(i, Math.min(i + sampleBlockSize, samples));

    const mp3buf = channels === 2
      ? mp3encoder.encodeBuffer(leftChunk, rightChunk)
      : mp3encoder.encodeBuffer(leftChunk);

    if (mp3buf.length > 0) {
      mp3Data.push(mp3buf);
    }
  }

  // Flush remaining data
  const mp3buf = mp3encoder.flush();
  if (mp3buf.length > 0) {
    mp3Data.push(mp3buf);
  }

  // Combine all MP3 chunks into a single blob
  const blob = new Blob(mp3Data, { type: 'audio/mpeg' });

  console.log(`[AudioConcat] MP3 encoding complete: ${(blob.size / 1024 / 1024).toFixed(2)} MB`);

  return blob;
}

/**
 * Check if all files are MP3 format
 */
function areAllMp3Files(files: AudioFile[]): boolean {
  return files.every(f => {
    const ext = f.file_name.split('.').pop()?.toLowerCase();
    return ext === 'mp3';
  });
}

/**
 * Find the size of ID3v2 tag at the beginning of an MP3 file
 * ID3v2 header: "ID3" + version (2 bytes) + flags (1 byte) + size (4 bytes, syncsafe)
 * Returns 0 if no ID3v2 tag found
 */
function getID3v2Size(data: Uint8Array): number {
  // Check for "ID3" header
  if (data.length < 10 || data[0] !== 0x49 || data[1] !== 0x44 || data[2] !== 0x33) {
    return 0;
  }

  // Size is stored in 4 bytes using syncsafe integers (7 bits per byte)
  const size = ((data[6]! & 0x7f) << 21) |
               ((data[7]! & 0x7f) << 14) |
               ((data[8]! & 0x7f) << 7) |
               (data[9]! & 0x7f);

  // Total size = 10 byte header + tag size
  return 10 + size;
}

/**
 * Check if file has ID3v1 tag at the end (128 bytes starting with "TAG")
 */
function hasID3v1Tag(data: Uint8Array): boolean {
  if (data.length < 128) return false;

  const tagStart = data.length - 128;
  return data[tagStart] === 0x54 && // 'T'
         data[tagStart + 1] === 0x41 && // 'A'
         data[tagStart + 2] === 0x47;   // 'G'
}

/**
 * Strip ID3 metadata from an MP3 buffer
 * Removes ID3v2 tag from beginning and ID3v1 tag from end
 */
function stripID3Tags(buffer: ArrayBuffer): Uint8Array {
  const data = new Uint8Array(buffer);

  // Find start of audio data (after ID3v2 tag)
  const id3v2Size = getID3v2Size(data);

  // Find end of audio data (before ID3v1 tag)
  const hasV1 = hasID3v1Tag(data);
  const endOffset = hasV1 ? data.length - 128 : data.length;

  // Return slice without metadata
  if (id3v2Size > 0 || hasV1) {
    console.log(`[AudioConcat] Stripped ID3 tags: v2=${id3v2Size} bytes, v1=${hasV1 ? 128 : 0} bytes`);
    return data.slice(id3v2Size, endOffset);
  }

  return data;
}

/**
 * Fast binary concatenation for MP3 files with ID3 tag handling
 *
 * Keeps metadata from first file, strips from subsequent files to avoid
 * playback issues from multiple ID3 tags in concatenated output.
 *
 * Note: This is a fallback - server-side concatenation is preferred.
 */
async function binaryConcatenateMp3Files(
  arrayBuffers: ArrayBuffer[],
  onProgress?: (progress: ConcatenationProgress) => void,
  totalFiles?: number
): Promise<{ blob: Blob; estimatedDuration: number }> {
  console.log(`[AudioConcat] Using FAST binary MP3 concatenation (no decode/encode)`);

  if (arrayBuffers.length === 0) {
    return { blob: new Blob([], { type: 'audio/mpeg' }), estimatedDuration: 0 };
  }

  onProgress?.({
    stage: 'concatenating',
    progress: 50,
    totalFiles,
  });

  // Process buffers: strip ID3 tags from ALL files to produce clean MP3 stream
  // This removes ID3v2 headers (start) and ID3v1 tags (end) from every file,
  // preventing mid-stream corruption and removing language metadata (TLAN frame)
  // that could confuse transcription services.
  const processedBuffers: Uint8Array[] = [];

  for (let i = 0; i < arrayBuffers.length; i++) {
    processedBuffers.push(stripID3Tags(arrayBuffers[i]!));
  }

  // Calculate total size after stripping
  const totalSize = processedBuffers.reduce((sum, buf) => sum + buf.byteLength, 0);
  console.log(`[AudioConcat] Total binary size: ${(totalSize / 1024 / 1024).toFixed(2)} MB`);

  // Concatenate all buffers into one
  const combinedBuffer = new Uint8Array(totalSize);
  let offset = 0;

  for (const buffer of processedBuffers) {
    combinedBuffer.set(buffer, offset);
    offset += buffer.byteLength;
  }

  onProgress?.({
    stage: 'encoding',
    progress: 80,
    totalFiles,
  });

  // Create blob (no encoding needed!)
  const blob = new Blob([combinedBuffer], { type: 'audio/mpeg' });

  // Estimate duration based on typical MP3 bitrate (~128kbps = 16KB/s)
  // Note: This is approximate for VBR files but sufficient for UI display
  const estimatedDuration = totalSize / (128 * 1024 / 8);

  console.log(`[AudioConcat] Binary concatenation complete: ${(blob.size / 1024 / 1024).toFixed(2)} MB`);
  console.log(`[AudioConcat] Estimated duration: ${estimatedDuration.toFixed(1)}s`);

  return { blob, estimatedDuration };
}

/**
 * Slow decode/encode concatenation for mixed formats
 * Required when files have different formats (MP3 + WAV, etc.)
 */
async function decodeEncodeConcatenate(
  arrayBuffers: ArrayBuffer[],
  sortedFiles: AudioFile[],
  onProgress?: (progress: ConcatenationProgress) => void,
  totalFiles?: number
): Promise<{ blob: Blob; duration: number }> {
  console.log(`[AudioConcat] Using SLOW decode/encode concatenation (mixed formats)`);

  // Initialize Crunker
  const crunker = new Crunker();

  // Stage 2: Decode audio buffers
  onProgress?.({
    stage: 'decoding',
    progress: 25,
    totalFiles,
  });

  // Convert ArrayBuffers to Blobs for Crunker
  const blobs = arrayBuffers.map((buffer, index) => {
    const file = sortedFiles[index]!;
    const ext = file.file_name.split('.').pop()?.toLowerCase() || 'mp3';
    const mimeType = ext === 'wav' ? 'audio/wav' :
                     ext === 'm4a' ? 'audio/mp4' :
                     ext === 'ogg' ? 'audio/ogg' :
                     ext === 'webm' ? 'audio/webm' :
                     ext === 'flac' ? 'audio/flac' :
                     'audio/mpeg';
    return new Blob([buffer], { type: mimeType });
  });

  // Create object URLs (store them so we can revoke later to prevent memory leak)
  const objectUrls = blobs.map(b => URL.createObjectURL(b));

  // Fetch audio buffers using Crunker (this decodes the audio)
  const audioBuffers = await crunker.fetchAudio(...objectUrls);
  console.log(`[AudioConcat] Decoded ${audioBuffers.length} audio buffers`);

  // Stage 3: Concatenate audio
  onProgress?.({
    stage: 'concatenating',
    progress: 50,
    totalFiles,
  });

  const concatenatedBuffer = crunker.concatAudio(audioBuffers);
  const duration = concatenatedBuffer.duration;
  console.log(`[AudioConcat] Concatenated audio duration: ${duration.toFixed(1)}s`);

  // Stage 4: Export to MP3 (much smaller than WAV)
  onProgress?.({
    stage: 'encoding',
    progress: 70,
    totalFiles,
  });

  // Encode to MP3 at 128kbps (good quality, ~1MB per minute)
  const blob = encodeAudioBufferToMp3(concatenatedBuffer, 128);
  console.log(`[AudioConcat] MP3 blob size: ${(blob.size / 1024 / 1024).toFixed(2)} MB`);

  // Clean up object URLs to prevent memory leak
  objectUrls.forEach(url => URL.revokeObjectURL(url));

  return { blob, duration };
}

/**
 * Concatenate multiple audio files and upload the result to storage
 *
 * Uses FAST binary concatenation for MP3-only files (no decode/encode)
 * Falls back to SLOW decode/encode for mixed formats
 *
 * @param files - Array of audio files to concatenate
 * @param sessionId - Session ID for storage path
 * @param organizationId - Organization ID for storage path
 * @param onProgress - Progress callback
 * @returns The concatenated audio blob and metadata
 */
export async function concatenateAndUploadAudio(
  files: AudioFile[],
  sessionId: string,
  organizationId: string,
  onProgress?: (progress: ConcatenationProgress) => void
): Promise<ConcatenationResult> {
  const startTime = Date.now();

  // Sort files in the correct order
  const sortedFiles = sortAudioFiles(files);
  const totalFiles = sortedFiles.length;

  console.log(`[AudioConcat] Starting concatenation of ${totalFiles} files`);
  console.log(`[AudioConcat] Order: ${sortedFiles.map(f => `${f.file_name} (${getAudioTypeLabel(f.file_name)})`).join(' → ')}`);

  // Check if we can use fast binary concatenation (all MP3s)
  const useFastPath = areAllMp3Files(sortedFiles);
  console.log(`[AudioConcat] All files are MP3: ${useFastPath} → using ${useFastPath ? 'FAST binary' : 'SLOW decode/encode'} path`);

  // Stage 1: Download all audio files
  onProgress?.({
    stage: 'downloading',
    progress: 0,
    totalFiles,
  });

  const audioBufferPromises: Promise<ArrayBuffer>[] = sortedFiles.map(async (file, index) => {
    onProgress?.({
      stage: 'downloading',
      currentFile: file.file_name,
      fileIndex: index + 1,
      totalFiles,
      progress: Math.round((index / totalFiles) * 25),
    });

    return downloadAudioFile(file.storage_path);
  });

  const arrayBuffers = await Promise.all(audioBufferPromises);
  console.log(`[AudioConcat] Downloaded ${arrayBuffers.length} files`);

  // Choose concatenation strategy based on file formats
  let audioBlob: Blob;
  let duration: number;

  if (useFastPath) {
    // FAST: Binary concatenation (no decode/encode)
    const result = await binaryConcatenateMp3Files(arrayBuffers, onProgress, totalFiles);
    audioBlob = result.blob;
    duration = result.estimatedDuration;
  } else {
    // SLOW: Decode, concatenate, encode
    const result = await decodeEncodeConcatenate(arrayBuffers, sortedFiles, onProgress, totalFiles);
    audioBlob = result.blob;
    duration = result.duration;
  }

  // Stage 5: Upload to storage
  onProgress?.({
    stage: 'uploading',
    progress: 80,
    totalFiles,
  });

  const timestamp = Date.now();
  const combinedFileName = `combined_${timestamp}.mp3`;
  const storagePath = `${organizationId}/${sessionId}/${combinedFileName}`;

  console.log(`[AudioConcat] Uploading to: ${storagePath} (${(audioBlob.size / (1024 * 1024)).toFixed(1)}MB)`);

  if (shouldUseTus(audioBlob.size)) {
    // TUS resumable upload for large concatenated files
    await uploadWithTus(audioBlob, {
      bucket: 'session-files',
      objectPath: storagePath,
      contentType: 'audio/mpeg',
      upsert: false,
      onProgress: (info) => {
        // Map TUS progress into the uploading stage (80-95%)
        onProgress?.({
          stage: 'uploading',
          progress: 80 + Math.round(info.progress * 0.15),
          totalFiles,
        });
      },
    });
  } else {
    const { error: uploadError } = await supabase.storage
      .from('session-files')
      .upload(storagePath, audioBlob, {
        cacheControl: '3600',
        upsert: false,
        contentType: 'audio/mpeg',
      });

    if (uploadError) {
      throw new Error(`Failed to upload concatenated audio: ${uploadError.message}`);
    }
  }

  // Create file record in database
  const { data: { user } } = await supabase.auth.getUser();

  const { data: fileData, error: fileError } = await supabase
    .from('files')
    .insert({
      organization_id: organizationId,
      session_id: sessionId,
      uploaded_by: user?.id,
      file_name: combinedFileName,
      file_type: 'audio',
      mime_type: 'audio/mpeg',
      file_size: audioBlob.size,
      storage_path: storagePath,
    })
    .select('id')
    .single();

  if (fileError) {
    throw new Error(`Failed to create file record: ${fileError.message}`);
  }

  // Stage 6: Complete
  onProgress?.({
    stage: 'complete',
    progress: 100,
    totalFiles,
  });

  const elapsedTime = ((Date.now() - startTime) / 1000).toFixed(1);
  console.log(`[AudioConcat] Concatenation complete in ${elapsedTime}s`);
  console.log(`[AudioConcat] Combined file: ${storagePath}`);

  return {
    blob: audioBlob,
    storagePath,
    fileId: fileData.id,
    duration,
    sourceFiles: sortedFiles.map(f => f.file_name),
    sourceFileIds: sortedFiles.map(f => f.id),
    sourceStoragePaths: sortedFiles.map(f => f.storage_path),
  };
}

/**
 * Get a human-readable description of the concatenation order
 */
export function getConcatenationOrderDescription(files: AudioFile[]): string {
  const sortedFiles = sortAudioFiles(files);

  return sortedFiles
    .map((f, index) => `${index + 1}. ${f.file_name} (${getAudioTypeLabel(f.file_name)})`)
    .join('\n');
}

/**
 * Delete the original source audio files after successful concatenation
 * This removes both the storage files and database records
 *
 * Uses bulk operations for efficiency (single API call per resource type)
 *
 * Order is important: storage first, then database.
 * If storage deletion fails, we do NOT delete database records to avoid
 * creating orphaned files (files in storage with no tracking record).
 *
 * @param sourceFileIds - Array of file IDs to delete from database
 * @param sourceStoragePaths - Array of storage paths to delete from storage
 * @returns Object with deletion results
 */
export async function deleteSourceAudioFiles(
  sourceFileIds: string[],
  sourceStoragePaths: string[]
): Promise<{ deleted: number; errors: string[] }> {
  const errors: string[] = [];

  console.log(`[AudioConcat] Deleting ${sourceFileIds.length} source audio files (bulk operation)...`);

  // Step 1: Delete from storage FIRST (bulk operation)
  if (sourceStoragePaths.length > 0) {
    try {
      const { data, error } = await supabase.storage
        .from('session-files')
        .remove(sourceStoragePaths);

      if (error) {
        // Storage deletion failed - do NOT proceed to database deletion
        // This prevents orphaned files in storage
        console.error(`[AudioConcat] Failed to delete storage files:`, error);
        console.warn(`[AudioConcat] Skipping database deletion to prevent orphaned files`);
        errors.push(`Storage bulk delete: ${error.message}`);
        return { deleted: 0, errors };
      }

      const storageDeleted = data?.length ?? sourceStoragePaths.length;
      console.log(`[AudioConcat] Deleted ${storageDeleted} files from storage`);
    } catch (err) {
      // Storage deletion threw - do NOT proceed to database deletion
      console.error(`[AudioConcat] Error deleting storage files:`, err);
      console.warn(`[AudioConcat] Skipping database deletion to prevent orphaned files`);
      errors.push(`Storage bulk delete: ${(err as Error).message}`);
      return { deleted: 0, errors };
    }
  }

  // Step 2: Only delete from database if storage deletion succeeded
  let dbDeleted = 0;
  if (sourceFileIds.length > 0) {
    try {
      const { error, count } = await supabase
        .from('files')
        .delete({ count: 'exact' })
        .in('id', sourceFileIds);

      if (error) {
        // Database deletion failed but storage already deleted
        // Log error but files are already gone from storage
        console.error(`[AudioConcat] Failed to delete file records:`, error);
        console.warn(`[AudioConcat] Storage files deleted but database records remain (manual cleanup needed)`);
        errors.push(`Database bulk delete: ${error.message}`);
      } else {
        dbDeleted = count ?? sourceFileIds.length;
        console.log(`[AudioConcat] Deleted ${dbDeleted} file records from database`);
      }
    } catch (err) {
      console.error(`[AudioConcat] Error deleting file records:`, err);
      console.warn(`[AudioConcat] Storage files deleted but database records remain (manual cleanup needed)`);
      errors.push(`Database bulk delete: ${(err as Error).message}`);
    }
  }

  console.log(`[AudioConcat] Deleted ${dbDeleted}/${sourceFileIds.length} source files`);
  if (errors.length > 0) {
    console.warn(`[AudioConcat] ${errors.length} deletion errors occurred`);
  }

  return { deleted: dbDeleted, errors };
}

/**
 * Server-side audio concatenation result
 */
export interface ServerConcatenationResult {
  success: boolean;
  combinedFile?: {
    id: string;
    fileName: string;
    storagePath: string;
    fileSize: number;
    estimatedDuration: number;
  };
  deletedFiles?: number;
  elapsedTime?: number;
  error?: string;
}

/**
 * Concatenate audio files server-side via API endpoint
 * MUCH faster than client-side because server is in same datacenter as Supabase
 *
 * @param files - Array of audio files to concatenate (will be sorted by the function)
 * @param sessionId - Session ID for storage path
 * @param organizationId - Organization ID for storage path
 * @param deleteSourceFiles - Whether to delete source files after concatenation (default: true)
 * @param onProgress - Progress callback
 * @returns Server concatenation result with combined file info
 */
export async function concatenateAudioServerSide(
  files: AudioFile[],
  sessionId: string,
  organizationId: string,
  deleteSourceFiles: boolean = true,
  onProgress?: (progress: ConcatenationProgress) => void
): Promise<ConcatenationResult> {
  const startTime = Date.now();

  // Sort files in the correct order
  const sortedFiles = sortAudioFiles(files);
  const totalFiles = sortedFiles.length;

  console.log(`[AudioConcat] Starting SERVER-SIDE concatenation of ${totalFiles} files`);
  console.log(`[AudioConcat] Order: ${sortedFiles.map(f => `${f.file_name} (${getAudioTypeLabel(f.file_name)})`).join(' → ')}`);

  // Stage: Sending to server
  onProgress?.({
    stage: 'downloading', // Using 'downloading' stage to indicate server processing
    progress: 10,
    totalFiles,
  });

  // Prepare request body
  const requestBody = {
    sessionId,
    organizationId,
    files: sortedFiles.map(f => ({
      id: f.id,
      file_name: f.file_name,
      storage_path: f.storage_path,
    })),
    deleteSourceFiles,
  };

  onProgress?.({
    stage: 'concatenating',
    progress: 30,
    totalFiles,
  });

  // Get the current session token for authentication
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('No authentication session found. Please log in again.');
  }

  // Call server-side API
  const response = await fetch(`${API_BASE_URL}/api/concatenate-audio`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Server concatenation failed: ${response.status} - ${errorText}`);
  }

  const result: ServerConcatenationResult = await response.json();

  if (!result.success || !result.combinedFile) {
    throw new Error(result.error || 'Server concatenation failed with unknown error');
  }

  onProgress?.({
    stage: 'complete',
    progress: 100,
    totalFiles,
  });

  const elapsedTime = ((Date.now() - startTime) / 1000).toFixed(1);
  console.log(`[AudioConcat] Server-side concatenation complete in ${elapsedTime}s (server time: ${result.elapsedTime?.toFixed(1)}s)`);
  console.log(`[AudioConcat] Combined file: ${result.combinedFile.storagePath}`);
  console.log(`[AudioConcat] File size: ${(result.combinedFile.fileSize / 1024 / 1024).toFixed(2)} MB`);
  if (deleteSourceFiles) {
    console.log(`[AudioConcat] Deleted ${result.deletedFiles} source files`);
  }

  // Return in same format as client-side function for compatibility
  // Note: blob is omitted for server-side concatenation (file is already on server)
  return {
    storagePath: result.combinedFile.storagePath,
    fileId: result.combinedFile.id,
    duration: result.combinedFile.estimatedDuration,
    sourceFiles: sortedFiles.map(f => f.file_name),
    sourceFileIds: sortedFiles.map(f => f.id),
    sourceStoragePaths: sortedFiles.map(f => f.storage_path),
  };
}
